import { FunctionComponent, ReactNode, useState } from 'react'
import styled from 'styled-components'
import Input from '../../../../components/Input'
import { PasswordVisibilityIcon } from '../../../authentication/authFormCommon'
import { styles } from '../../../../styles/themes/style'
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers'

/*
 * API keys stay hidden until the viewer asks to see them, like a password field:
 * these pages end up on shared screens and in demo recordings.
 */

export const MASKED_API_KEY = '•'.repeat(24)

/** Dots and the last 4 characters: enough to tell keys apart, nothing usable on a recording. */
export const maskApiKey = (key: string, dots = 28) => `${'•'.repeat(dots)}${key.slice(-4)}`

/** Copies a key with the usual toast; always the full key, whatever is on screen. */
export const useCopyApiKey = () => {
    const { showSuccessToast } = useAppHandlers()

    return (key: string) => {
        if (!key || !navigator?.clipboard?.writeText) return
        navigator.clipboard.writeText(key).then(() => showSuccessToast({message: 'API key copied to clipboard.'}))
    }
}

interface IApiKeyVisibilityToggleProps {
    visible: boolean
    onToggle: () => void
}

/** The sign-in page's show/hide password eye, for an API key. */
export const ApiKeyVisibilityToggle: FunctionComponent<IApiKeyVisibilityToggleProps> = ({ visible, onToggle }) => (
    <ToggleButton
        type='button'
        onClick={onToggle}
        aria-label={visible ? 'Hide API key' : 'Show API key'}
        title={visible ? 'Hide API key' : 'Show API key'}
    >
        <PasswordVisibilityIcon visible={visible} />
    </ToggleButton>
)

interface IApiKeyInputProps {
    value: string
}

/** Read-only API key field: masked by default, eye to show or hide, double-click to copy the full key. */
export const ApiKeyInput: FunctionComponent<IApiKeyInputProps> = ({ value }) => {
    const [visible, setVisible] = useState(false)
    const copyApiKey = useCopyApiKey()

    return (
        <InputWrap>
            <Input
                readOnly
                prefilledValue={visible ? value : maskApiKey(value)}
                onDoubleClick={() => copyApiKey(value)}
                spellCheck={false}
            />
            <InputToggleSlot>
                <ApiKeyVisibilityToggle visible={visible} onToggle={() => setVisible(previous => !previous)} />
            </InputToggleSlot>
        </InputWrap>
    )
}

interface IApiKeyCodeBlockProps {
    visible: boolean
    onToggle: () => void
    // Leaves out the eye when there is no real key to show (the YOUR_API_KEY placeholder).
    showToggle: boolean
    children: ReactNode
}

/** Wraps a code block that contains the API key and puts the eye in its top-right corner. */
export const ApiKeyCodeBlock: FunctionComponent<IApiKeyCodeBlockProps> = ({ visible, onToggle, showToggle, children }) => (
    <CodeBlockWrap $withToggle={showToggle}>
        {children}
        {showToggle && (
            <CodeBlockToggleSlot>
                <ApiKeyVisibilityToggle visible={visible} onToggle={onToggle} />
            </CodeBlockToggleSlot>
        )}
    </CodeBlockWrap>
)

const ToggleButton = styled.button`
    width: 20px;
    height: 20px;
    padding: 0;
    border: none;
    background: transparent;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    color: ${styles.colors.black300};

    &:hover { color: ${styles.colors.primary600}; }
    &:focus { outline: none; }
`
const InputWrap = styled.div`
    position: relative;
    width: 100%;

    input {
        padding-right: 38px;
        font-family: monospace;
        font-size: 13px;
        letter-spacing: 0.02em;
        text-overflow: ellipsis;
    }
`
const InputToggleSlot = styled.div`
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    display: flex;
`
const CodeBlockWrap = styled.div<{ $withToggle: boolean }>`
    position: relative;

    ${({ $withToggle }) => $withToggle && 'pre { padding-right: 44px; }'}
`
const CodeBlockToggleSlot = styled.div`
    position: absolute;
    top: 14px;
    right: 14px;
    display: flex;
`
